/**
 * Frontend constants for the platform-admin cross-env project clone.
 *
 * Target = sandbox1 always (v1). Source = whichever env this build is running on.
 * The "Clone for debugging" button on source envs (sandbox2 / production) is a
 * pure deeplink to sandbox1's clone landing page; sandbox1 does the actual work.
 */

/** Sandbox1's public web URL — the always-target for cross-env clones. */
export const SANDBOX1_FRONTEND_URL = 'https://sandbox1.optiedge.cc';

export type CloneEnv = 'sandbox1' | 'sandbox2' | 'production' | 'unknown';

/**
 * Detect which env this frontend build is currently running against.
 * Uses VITE_API_URL — different per env at build/deploy time.
 */
export function getCurrentEnvironment(): CloneEnv {
  const apiUrl = (import.meta.env.VITE_API_URL ?? '').toLowerCase();
  if (apiUrl.includes('api-sandbox1')) return 'sandbox1';
  if (apiUrl.includes('api-sandbox2')) return 'sandbox2';
  if (apiUrl.includes('api-app.optidev.ai')) return 'production';
  return 'unknown';
}

/**
 * Whether this env can act as a clone *source* (i.e. the "Clone for debugging"
 * deeplink button should appear here, gated by platform-admin).
 *
 * sandbox1 is the always-target — no point cloning sandbox1 → sandbox1 in v1.
 */
export function canBeCloneSource(env: CloneEnv = getCurrentEnvironment()): boolean {
  return env === 'sandbox2' || env === 'production';
}

/**
 * Build the cross-env deeplink to sandbox1's clone landing page.
 */
export function buildCloneDeeplink(args: {
  sourceEnv: 'sandbox2' | 'production';
  sourceProjectId: string;
}): string {
  const params = new URLSearchParams({
    sourceEnv: args.sourceEnv,
    sourceProjectId: args.sourceProjectId,
  });
  return `${SANDBOX1_FRONTEND_URL}/dashboard/clone?${params.toString()}`;
}
